import { Injectable } from '@angular/core';  
import { HttpEvent, HttpInterceptor, HttpHandler, HttpRequest, HttpErrorResponse } from '@angular/common/http';  
import { Observable, throwError, BehaviorSubject, of } from 'rxjs';  
import { catchError, filter, finalize, switchMap, take } from 'rxjs/operators';  
import { MsalService } from '@azure/msal-angular';  
import { EventType, BrowserAuthError } from '@azure/msal-browser';  
import { CommonService } from '../services/common-service';  
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';

@Injectable()  
export class AuthInterceptor implements HttpInterceptor {  
 private isRefreshing = false;  
 private tokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null);  
 private apiDomain: string = environment.apiBaseUrl;  
 private tokenExpirationTime: number | null = null;  
 private readonly refreshTokenBeforeMinutesOfExpiry = environment.refreshTokenBeforeMinutesOfExpiry;  
 private readonly apiScopes = environment.msal.protectedResourceMap.find(([url]: [any]) => url.replace(/\/$/, '') === this.apiDomain.replace(/\/$/, ''))?.[1] || [];
 private channel: BroadcastChannel;  
 private apiQueue: any = {};  
 private apiQueueLoaderStatus: { [key: string]: boolean } = {};

  constructor(private msalService: MsalService, private commonService: CommonService, private router: Router) {
   this.channel = new BroadcastChannel('auth_channel');  
   this.msalService.instance.addEventCallback((event) => {  
     if (event.eventType === EventType.LOGIN_FAILURE || event.eventType === EventType.LOGOUT_SUCCESS) {  
       this.logout();  
       this.tokenSubject.next(null);  
     }  
   });  
   this.channel.onmessage = (event) => {  
     if (event.data === 'logout') {  
       this.handleLogout();  
     }  
   };  
 }  

 intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {  
   //console.log('AuthInterceptor intercept called:', request.url);  
   if (!request.url.startsWith(this.apiDomain)) {  
     return next.handle(request); // Skip for other domains  
   }  
    this.apiQueue[request.url] = true;
    // Skip loader for autosave requests
    const skipLoader = request.headers.has('X-Skip-Loader') && request.headers.get('X-Skip-Loader') === 'true';
    this.apiQueueLoaderStatus[request.url] = !skipLoader;
    if (!skipLoader) {
      this.commonService.showLoading(request.url);
    }

   if (this.shouldRefreshToken()) {  
     return this.acquireToken(request).pipe(  
       switchMap(updatedRequest => next.handle(updatedRequest)),  
       catchError(error => this.handleError(error, request, next)),  
       finalize(() => {  
         delete this.apiQueue[request.url];  
         const loaderWasShown = this.apiQueueLoaderStatus[request.url];
         delete this.apiQueueLoaderStatus[request.url];
         if (Object.keys(this.apiQueue).length === 0 && loaderWasShown)  
           this.commonService.hideLoading();  
       })  
     );  
   } else {  
     return this.addToken(request).pipe(  
       switchMap(updatedRequest => next.handle(updatedRequest)),  
       catchError(error => this.handleError(error, request, next)),  
       finalize(() => {  
         delete this.apiQueue[request.url];  
         const loaderWasShown = this.apiQueueLoaderStatus[request.url];
         delete this.apiQueueLoaderStatus[request.url];
         if (Object.keys(this.apiQueue).length === 0 && loaderWasShown)  
           this.commonService.hideLoading();  
       })  
     );  
   }  
 }  

 private addToken(request: HttpRequest<any>): Observable<HttpRequest<any>> {  
   const token = this.tokenSubject.value;  
   if (token) {  
     return of(this.cloneRequestWithToken(request, token));  
   } else {  
     return this.acquireToken(request);  
   }  
 }  

 private acquireToken(request: HttpRequest<any>): Observable<HttpRequest<any>> {  
   if (!this.isRefreshing) {  
     this.isRefreshing = true;  
     const account = this.msalService.instance.getActiveAccount() || this.msalService.instance.getAllAccounts()[0];  
     return this.msalService.acquireTokenSilent({  
       scopes: this.apiScopes,  
       account: account,  
       forceRefresh: false  
     }).pipe(  
       switchMap(tokenResponse => {  
         this.isRefreshing = false;  
         const newToken = tokenResponse.accessToken;  
         if (tokenResponse.expiresOn instanceof Date) {  
           this.tokenExpirationTime = tokenResponse.expiresOn.getTime();  
         } else if (typeof tokenResponse.expiresOn === 'string') {  
           this.tokenExpirationTime = new Date(tokenResponse.expiresOn).getTime();  
         } else {  
           this.tokenExpirationTime = null;  
         }  
         this.tokenSubject.next(newToken);  
         return of(this.cloneRequestWithToken(request, newToken));  
       }),  
       catchError(err => {  
         this.isRefreshing = false;
         this.tokenSubject.next(null);

         // Handle MSAL browser errors (like monitor_window_timeout)
         if (
           (err instanceof BrowserAuthError && err.errorCode === 'monitor_window_timeout') ||
           (err.errorCode && err.errorCode === 'monitor_window_timeout')
         ) {
           this.msalService.loginRedirect();
           return throwError(() => new Error('Session expired. Redirecting to login.'));
         }
         return throwError(() => new Error(err));  
       })  
     );  
   } else {  
     return new Observable<HttpRequest<any>>(observer => {  
       const subscription = this.tokenSubject.pipe(  
         filter(token => token != null),  
         take(1)  
       ).subscribe({  
         next: (token) => {  
           observer.next(this.cloneRequestWithToken(request, token));  
           observer.complete();  
         },  
         error: (err) => {  
           observer.error(err);  
           observer.complete();  
         }  
       });  
       return () => subscription.unsubscribe();  
     });  
   }  
 }  

 private handleError(error: any, request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {  
   if (error instanceof HttpErrorResponse) {  
     if (error.status === 401) {  
       sessionStorage.setItem('isAuthorized', 'false');  
       return throwError(() => new Error('Unauthorized Request'));  
     } 
     else if (error.status === 403) {
       if (error.error == 'Access denied')
         this.router.navigate(['/denied']);
       return this.acquireToken(request).pipe(  
         switchMap(updatedRequest => next.handle(updatedRequest)),  
         catchError(err => {  
           sessionStorage.setItem('isAuthorized', 'false');  
           return throwError(() => 'Forbidden request after token refresh attempt');  
         })  
       );  
     }  
   }  
   return throwError(error);  
 }  

 private cloneRequestWithToken(request: HttpRequest<any>, token: string | null): HttpRequest<any> {  
   return request.clone({  
     setHeaders: {  
       Authorization: `Bearer ${token}`  
     }  
   });  
 }  

 private shouldRefreshToken(): boolean {  
   const token = this.tokenSubject.value;  
   const currentTime = Date.now();  
   if (!token) return true;  
   if (this.tokenExpirationTime == null) return true;  
   if (currentTime >= this.tokenExpirationTime - this.refreshTokenBeforeMinutesOfExpiry && currentTime < this.tokenExpirationTime) return true;  
   return false;  
 }  

 private handleLogout(): void {  
   this.tokenSubject.next(null);  
   this.msalService.logoutRedirect();  
   sessionStorage.clear();  
   sessionStorage.setItem('isAuthorized', 'false');  
 }  

 public logout(): void {  
   this.channel.postMessage('logout');  
   this.tokenSubject.next(null);  
 }  
}
